import React, { useState, useMemo } from 'react';
import Header from '@/components/common/Header';
import { useCompanyStats } from '@/hooks/useCompanyStats';
import { useDriverLeaderboard } from '@/hooks/useDriverLeaderboard';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { DriverLeaderboard } from './components/DriverLeaderboard';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Loader2, AlertCircle, Clock, DollarSign, Phone, Users } from 'lucide-react';

interface AnalyticsScreenProps {
  showHeader?: boolean;
  onDriverClick?: (driverId: string, driverName: string) => void;
}

type TimeRange = 'day' | 'week' | 'month';

const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  subtitle?: string;
  variant?: 'default' | 'success' | 'info';
}> = ({ label, value, icon, subtitle, variant = 'default' }) => {
  const getBackgroundColor = () => {
    switch (variant) {
      case 'success':
        return '#f0fdf4'; // green-50
      case 'info':
        return '#eff6ff'; // blue-50
      default:
        return '#ffffff'; // white
    }
  };

  const getBorderColor = () => {
    switch (variant) {
      case 'success':
        return '#bbf7d0'; // green-200
      case 'info':
        return '#bfdbfe'; // blue-200
      default:
        return '#e5e7eb'; // gray-200
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'success':
        return '#15803d'; // green-700
      case 'info':
        return '#1d4ed8'; // blue-700
      default:
        return '#000000'; // black
    }
  };

  return (
    <div
      className="border rounded-lg p-4"
      style={{ backgroundColor: getBackgroundColor(), borderColor: getBorderColor() }}
    >
      <div className="flex items-center gap-2 mb-2">
        {icon && <span className="text-gray-500">{icon}</span>}
        <div className="text-gray-600 text-sm font-medium">{label}</div>
      </div>
      <div className="text-2xl font-bold" style={{ color: getTextColor() }}>{value}</div>
      {subtitle && (
        <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
      )}
    </div>
  );
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatMinutes = (minutes: number | null | string | undefined): string => {
  if (minutes === null || minutes === undefined) return '--';
  const n = typeof minutes === 'number' ? minutes : Number(minutes);
  if (!Number.isFinite(n)) return '--';
  if (n < 60) return `${n.toFixed(1)} min`;
  const hours = Math.floor(n / 60);
  const mins = n % 60;
  return mins > 0 ? `${hours}h ${mins.toFixed(1)}m` : `${hours}h`;
};

const AnalyticsScreen: React.FC<AnalyticsScreenProps> = ({ showHeader = true, onDriverClick }) => {
  const { driver } = useDriverStateV2();
  const [timeRange, setTimeRange] = useState<TimeRange>('day');

  // Calculate date range based on selected time range
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case 'day':
        startDate.setDate(endDate.getDate()); // Today
        break;
      case 'week':
        startDate.setDate(endDate.getDate() - 7); // Past 7 days
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1); // Past month
        break;
    }

    return {
      startDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD format
      endDate: endDate.toISOString().split('T')[0],
    };
  }, [timeRange]);

  const { data: stats, isLoading, error } = useCompanyStats({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: leaderboardData, isLoading: isLeaderboardLoading } = useDriverLeaderboard({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="text-center text-red-600 max-w-sm">
          <AlertCircle size={48} className="mx-auto mb-4" />
          <p className="font-semibold mb-2">Unable to Load Analytics</p>
          <p className="text-sm">Please try again later</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const completionPercentage = stats.totalCalls > 0
    ? Math.round((stats.completedCalls / stats.totalCalls) * 100)
    : 0;

  return (
    <>
      {showHeader && (
        <Header
          driverName="Analytics"
          status="ACTIVE"
          onMenuClick={() => {}}
          onStatusClick={() => {}}
          onStatusChange={() => {}}
          isEmbeddedMode={false}
        />
      )}

      <div className="px-4 py-4">
        {/* Time Range Selector */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={(value) => value && setTimeRange(value as TimeRange)}
            className="justify-center"
          >
            <ToggleGroupItem
              value="day"
              className="px-6 py-2 rounded-lg"
              style={{
                backgroundColor: timeRange === 'day' ? '#45A17D' : 'transparent',
                color: timeRange === 'day' ? '#ffffff' : '#000000'
              }}
            >
              Day
            </ToggleGroupItem>
            <ToggleGroupItem
              value="week"
              className="px-6 py-2 rounded-lg"
              style={{
                backgroundColor: timeRange === 'week' ? '#45A17D' : 'transparent',
                color: timeRange === 'week' ? '#ffffff' : '#000000'
              }}
            >
              Week
            </ToggleGroupItem>
            <ToggleGroupItem
              value="month"
              className="px-6 py-2 rounded-lg"
              style={{
                backgroundColor: timeRange === 'month' ? '#45A17D' : 'transparent',
                color: timeRange === 'month' ? '#ffffff' : '#000000'
              }}
            >
              Month
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="space-y-6">
        {/* Calls Overview */}
        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}>
            <h2 className="text-xl font-semibold" style={{ color: '#000000' }}>Calls Overview</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Total Calls"
                value={stats.totalCalls}
                icon={<Phone size={16} />}
                subtitle="In current period"
              />
              <StatCard
                label="Completed"
                value={stats.completedCalls}
                icon={<Phone size={16} />}
                subtitle={`${completionPercentage}% completion rate`}
                variant="success"
              />
            </div>
          </div>
        </div>

        {/* Invoicing Section */}
        <div className="bg-background border border-gray-200 rounded-xl  shadow-sm overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign size={20} className="text-gray-600" />
              <h3 className="text-lg font-semibold text-black">Invoicing</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-black">
                  {formatCurrency(stats.totalInvoiced)}
                </div>
                <div className="text-xs text-gray-600 mt-1">Total Invoiced</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">
                  {formatCurrency(stats.totalPaid)}
                </div>
                <div className="text-xs text-green-600 mt-1">Total Paid</div>
              </div>
            </div>
            {stats.totalInvoiced > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Collection Rate</span>
                  <span>{Math.round((stats.totalPaid / stats.totalInvoiced) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min((stats.totalPaid / stats.totalInvoiced) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={20} className="text-gray-600" />
              <h3 className="text-lg font-semibold text-black">Performance</h3>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg" style={{ backgroundColor: '#eff6ff' }}>
              <div className="text-3xl font-bold text-blue-700">
                {formatMinutes(stats.averageCompletionTimeMinutes)}
              </div>
              <div className="text-xs text-blue-600 mt-1">Average Time to Complete</div>
              <div className="text-xs text-gray-500 mt-2">
                Based on {stats.completedCalls} completed call{stats.completedCalls !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Account Breakdown */}
        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users size={20} className="text-gray-600" />
              <h3 className="text-lg font-semibold text-black">Account Breakdown</h3>
            </div>
            {stats.accountBreakdown && stats.accountBreakdown.length > 0 ? (
              <div className="space-y-4">
                {stats.accountBreakdown.map((account, index) => {
                  const maxCalls = Math.max(...stats.accountBreakdown.map((a) => a.callCount), 1);
                  return (
                    <div key={account.accountId || index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800 truncate flex-1">
                          {account.accountName}
                        </span>
                        <span className="text-sm font-semibold text-black ml-2">
                          {account.callCount} call{account.callCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{
                            width: `${(account.callCount / maxCalls) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Invoiced: {formatCurrency(account.totalInvoiced)}</span>
                        <span>Paid: {formatCurrency(account.totalPaid)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users size={32} className="mx-auto mb-2 opacity-50" />
                <p>No account data available</p>
                <p className="text-xs mt-1">Calls without assigned accounts won't appear here</p>
              </div>
            )}
          </div>
        </div>

        {/* Driver Leaderboard */}
        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
          <DriverLeaderboard
            leaderboard={leaderboardData?.leaderboard}
            currentDriverId={driver?.id || ''}
            isLoading={isLeaderboardLoading}
            onDriverClick={onDriverClick}
          />
        </div>
        </div>
      </div>
    </>
  );
};

export default AnalyticsScreen;


